import { useRouter } from 'next/router'
import classes from './MainProductHero.module.scss'
import { useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import useScrollPercentage from '@/hooks/useScrollPercentage'
import { useMediaQuery } from 'react-responsive'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import { useCustomerContext } from '@/context/CustomerContext'
import { useModalContext } from '@/context/ModalContext'
import { getSelectedVariant } from '@/utils/getSelectedVariant'

import useProductReviewsDetails from '@/hooks/useProductReviewsDetails'
import useProductDiscountsInfo from '@/hooks/useProductDiscountsInfo'

import MainProductSlider from './MainProductSlider'
import MainProductHeroPricing from './MainProductHeroPricing'
import MainProductReview from './MainProductReview'
import MainProductDescription from './MainProductDescription'
import ProductOptions from './ProductOptions'
import ProductHeroQuantityInput from './ProductHeroQuantityInput'
import TrustBadge from './TrustBadge'
import WhyBuyFromUs from './WhyBuyFromUs'
import MainProductHeroImages from './MainProductHeroImages'
import { usePurchaseFlowContext } from '@/context/PurchaseFlowContext'
import FrequencyOptions from './FrequencyOptions'
import { getShellfishText } from '@/utils/getShellfishText'
import { dataLayerPDPAddToBasketClick } from '@/utils/dataLayer'

const MainProductHero = ({
  product,
  fields,
  selectedVariant,
  setSelectedVariant,
  whyBuyFromUsItems = [],
  reviewsRef,
  isSubscription,
  productPurchaseOptions
}) => {
  const { productInfoImages } = fields

  const variantsCount = product?.variants?.length ?? 0
  const isSubWithMultipleVariants = isSubscription && variantsCount > 1

  let productLocation = ''
  if (isSubWithMultipleVariants) {
    const shellfishText = getShellfishText(selectedVariant?.title)
    productLocation =  shellfishText || 'Fresh, wild-caught seafood every month.';
  } else if (isSubscription) {
    productLocation = 'Fresh, wild-caught seafood every month.'

    // on the reserve vox use the With Shellfish text
    if (product.handle?.includes('reserve')) {
      productLocation = 'With Shellfish'
    }

    // on the signature box use the Shellfish-free text
    if (product.handle?.includes('signature')) {
      productLocation = 'Shellfish-free'
    }
  } else {
    productLocation = fields?.productLocation ?? ''
  }
  // Get title and description based on product type
  const { title } = product
  const description = isSubscription ?
    product.metafields?.find(m => m.key === 'short_description')?.value :
    product.description

  const router = useRouter()
  const { variant: urlVariant } = router.query
  const availableForSale = selectedVariant?.availableForSale

  const variants = product.variants

  const [giftFormFields, setGiftFormFields] = useState({})
  const [selectedOptions, setSelectedOptions] = useState(
    selectedVariant.content?.selectedOptions,
  )

  const [scrollVariant, setScrollVariant] = useState(true)

  const { customer } = useCustomerContext()
  const productReviewInfo = useProductReviewsDetails(product?.handle?.current)

  const {
    hasApplicableCustomerDiscount,
    hasApplicableDiscount,
    hasApplicableNonCustomerDiscount,
    discountEnabledProduct,
  } = useProductDiscountsInfo(product, customer)

  const modalContext = useModalContext()
  const {
    data,
    addItemToOrder,
    getEligibleDiscounts,
  } = useHeadlessCheckoutContext()

  const purchaseFlowContext = usePurchaseFlowContext()
  const [frequency, setFrequency] = useState(
    purchaseFlowContext.options.frequency || 'monthly'
  )

  // Get available frequencies from product metafields
  const availableFrequencies = useMemo(() => {
    if (!isSubscription || !productPurchaseOptions?.subscription?.availableFrequencies) return []
    return productPurchaseOptions.subscription.availableFrequencies
  }, [isSubscription, productPurchaseOptions])

  const handleFrequencyChange = async (newFrequency) => {
    setFrequency(newFrequency)
    if (purchaseFlowContext) {
      await purchaseFlowContext.setOptions((options) => ({
        ...options,
        frequency: newFrequency,
      }))
    }
  }

  const openLoginModal = (e) => {
    e.preventDefault()
    modalContext.setIsOpen(false)
    modalContext.setModalType('login-noredirect')
    modalContext.setIsOpen(true)
  }

  const [quantity, setQuantity] = useState(1)
  const [isGift, setIsGift] = useState(false)
  const [isMobile, setIsMobile] = useState(true)

  const mobileMedia = useMediaQuery({ query: '(max-width: 767px)' })

  const percentage = useScrollPercentage()

  const eligibleDiscounts = useMemo(
    () => getEligibleDiscounts({ lineItems: [{ ...product }] }),
    [customer, product],
  )
  const [eligibleDiscount] = eligibleDiscounts

  const [membershipCost, setMembershipCost] = useState(selectedVariant?.price)
  useEffect(() => {
    if(eligibleDiscounts.length > 0) {
      setMembershipCost(eligibleDiscount?.getDiscountedPrice(selectedVariant?.price))
    }
  }
  , [eligibleDiscounts])

  const showDiscounts =
    hasApplicableCustomerDiscount || hasApplicableNonCustomerDiscount

  useEffect(() => {
    const recipient_name = data?.cart?.properties?.recipient_name;
    const recipient_email = data?.cart?.properties?.recipient_email;
    // make updates if cart is gift and PDP is not displaying gift info
    if (!isGift && (!!recipient_name || !!recipient_email)) {
      const newFormFields = { ...giftFormFields };
      if (recipient_name) newFormFields.recipient_name = recipient_name;
      if (recipient_email) newFormFields.recipient_email = recipient_email;
      setGiftFormFields(newFormFields); // set recipient info so it is shown in UI
      setIsGift(true); // display gift option to user
    }
  }, [data, isGift, giftFormFields]);

  const options = useMemo(() => {
    if (product?.content?.options?.some((option) => option.values.length > 1)) {
      return product?.content?.options
    }
    return null
  }, [product])

  const handleOptionChange = (value, option) => {
    const newOption = { name: option.name, value }
    const optionIndex = selectedOptions.findIndex((selectedOption) => {
      return selectedOption.name === newOption.name
    })

    const newSelectedOptions = [...selectedOptions]
    if (optionIndex > -1) {
      newSelectedOptions.splice(optionIndex, 1, newOption)
      setSelectedOptions([...newSelectedOptions])
    } else {
      setSelectedOptions([...newSelectedOptions, newOption])
    }
    const variant = getSelectedVariant({
      variants,
      options: newSelectedOptions,
    })
    setSelectedVariant(variant ? { ...variant } : null)
  }
  useEffect(() => {
    if (isSubscription && urlVariant && options?.length > 0) {
      // Be sure to work with string, not array
      const variantParam = Array.isArray(urlVariant) ? urlVariant[0] : urlVariant;
      // Remove '-' and convert 'no-shellfish' → 'no shellfish'
      const normalizedValue = variantParam.replace(/-/g, ' ').toLowerCase();
  
      // Search for the option that contains that value
      const optionItem = options.find(opt =>
        opt.values.map(v => v.toLowerCase()).includes(normalizedValue)
      );
  
      if (optionItem) {
        // Build the expected selectedOptions
        const newSelectedOptions = [
          { name: optionItem.name, value: optionItem.values.find(v =>
              v.toLowerCase() === normalizedValue
            )
          }
        ];
        setSelectedOptions(newSelectedOptions);
  
        // Get the variant 
        const variant = getSelectedVariant({
          variants,
          options: newSelectedOptions
        });
        if (variant) {
          setSelectedVariant(variant);
        }
      }
    }
  }, [isSubscription, urlVariant, options, variants, getSelectedVariant]);
  

  useEffect(() => {
    if (!isMobile) {
      setScrollVariant(percentage > 10 ? true : false)
    }
  }, [percentage, isMobile])

  const addToCart = useCallback(async () => {
    dataLayerPDPAddToBasketClick({
      product,
      variant: selectedVariant,
      quantity,
      url: typeof window !== 'undefined' ? window.location.pathname : '',
      context: 'pdp_add_to_basket',
    })

    await addItemToOrder({
      product,
      variant: selectedVariant,
      quantity,
      properties: isSubscription ? {
        interval_text: frequency.toString(),
        membership_type: 'subscription',
      } : {},
      openFlyout: true,
    });
  }, [product, selectedVariant, quantity, addItemToOrder, isSubscription, frequency]);

  const handleBuyNow = useCallback(async () => {

    await addItemToOrder({
      product,
      variant: selectedVariant,
      quantity,
      properties: isSubscription ? {
        interval_text: frequency.toString(),
        membership_type: 'subscription',
      } : {},
      redirectToCart: true,
    });
    router.push('/cart');
  }, [addItemToOrder, product, selectedVariant, quantity, router, isSubscription, frequency]);

  // Workaround because react-responsive doesn't work well with SSR
  useEffect(() => setIsMobile(mobileMedia), [mobileMedia])
  useEffect(() => {
    if (isMobile) {
      setScrollVariant(false)
    }
  }, [isMobile])

   const hasFreeShippingCode = data?.cart?.discountCodes.some(
    (d) => d.applicable && d.code === 'Ship with Next Order',
  )

  let shippingCost
  if (!hasFreeShippingCode) {
    const shippingCostArray = data?.cart?.deliveryGroups.nodes.map((group) =>
      parseFloat(group.selectedDeliveryOption?.estimatedCost.amount),
    )
    shippingCost =
      shippingCostArray?.length > 0
        ? shippingCostArray.reduce((total, cost) => (total += cost), 0)
        : NaN
  } else {
    shippingCost = 0
  }

  return (
    <>
      <div className={`${classes['product']} product`}>
        <div className={classes['product__inner']}>
          <div className={`${classes['product__row']} container`}>
            <div className={classes['slider']}>
              <MainProductSlider product={product} />
            </div>

            <div className={classes['main']}>
            {isMobile ? (
                <div
                  className={classNames(
                    classes['product-hero-header'],
                    isMobile && classes['product-hero-header-mobile'],
                  )}
                >
                  <div className={classes['product-hero-header-title-container']}>
                    <h1>{title}</h1>
                    <MainProductHeroPricing
                      product={product}
                      discountEnabledProduct={discountEnabledProduct}
                      onOpenLoginModal={openLoginModal}
                      eligibleDiscounts={eligibleDiscounts}
                      hasApplicableDiscount={hasApplicableDiscount}
                      options={options}
                      showDiscounts={showDiscounts}
                      selectedVariant={selectedVariant}
                      quantity={quantity}
                      pricingDisplay={product.pricingDisplay}
                      className="mobile-pricing"
                      isSubscription={isSubscription}
                    />
                  </div>

                  <div className={classes['location-review-container']}>
                    {productLocation && <h4 className={classes['location']}>{productLocation}</h4>}
                    {productReviewInfo && (
                      <MainProductReview productLocation={productLocation} productReviews={productReviewInfo} reviewsRef={reviewsRef} />
                    )}
                  </div>
                  <div className={classes['product-hero-price-options-container']}>
                    {!isSubscription && options && (
                      <ProductOptions
                        options={options}
                        handleOptionChange={handleOptionChange}
                        selectedVariant={selectedVariant}
                      />
                    )}
                    {isSubscription && availableFrequencies.length > 0 && (
                      <FrequencyOptions
                        availableFrequencies={availableFrequencies}
                        handleFrequencyChange={handleFrequencyChange}
                        frequency={frequency}
                      />
                    )}
                    {!isSubscription && (
                      <ProductHeroQuantityInput
                        onDecrease={() => setQuantity(quantity > 1 ? quantity - 1 : 1)}
                        onIncrease={() => setQuantity(quantity + 1)}
                      onChange={(event) => {
                        const value = event.target.value === '' ? '' : Math.max(1, Number(event.target.value));
                        setQuantity(value);
                      }}
                      availableForSale={availableForSale}
                      value={quantity}
                    />
                    )}
                  </div>
                  <TrustBadge isMobile={isMobile} />
                  <MainProductDescription description={description} />

                  <div className={`${classes["product-hero-purchase-container"]} ${classes["fixed-mobile"]}`}>
                    <button
                      className={classes["add-to-basket-btn"]}
                      disabled={!availableForSale || quantity === 0}
                      onClick={addToCart}
                    >
                    {!availableForSale ? 'Sold Out' : 'Add to Basket'}
                    </button>

                    {availableForSale && quantity !== 0 && (
                      <button
                        className={`${classes["add-to-basket-btn"]} ${classes["buy-now-btn"]}`}
                        disabled={!availableForSale || quantity === 0}
                        onClick={handleBuyNow}
                      >
                        Buy Now
                      </button>
                    )}
                  </div>
                  {whyBuyFromUsItems.length > 0 && (
                    <WhyBuyFromUs items={whyBuyFromUsItems} />
                  )}

                  {isSubscription && productInfoImages && (
                    <MainProductHeroImages
                      assets={productInfoImages.map(({ asset }) => asset)}
                    />
                  )}
                </div>
              ) : (
                <div className={classes['product-hero-header']}>
                  <h1>{title}</h1>
                  {productLocation && (
                    <div className={classes['product-hero-location-container']}>
                      <h4 className={classes['location']}>{productLocation}</h4>
                    </div>
                  )}
                  {productReviewInfo && (
                    <MainProductReview productLocation={productLocation} productReviews={productReviewInfo} reviewsRef={reviewsRef} />
                  )}

                  <MainProductDescription description={description} />

                  <div className={classes['product-hero-price-container']}>
                    <MainProductHeroPricing
                      product={product}
                      discountEnabledProduct={discountEnabledProduct}
                      onOpenLoginModal={openLoginModal}
                      eligibleDiscounts={eligibleDiscounts}
                      hasApplicableDiscount={hasApplicableDiscount}
                      options={options}
                      showDiscounts={showDiscounts}
                      selectedVariant={selectedVariant}
                      quantity={quantity}
                      pricingDisplay={product.pricingDisplay}
                      isSubscription={isSubscription}
                    />
                  </div>

                  {!isSubscription && options && (
                    <ProductOptions
                      options={options}
                      handleOptionChange={handleOptionChange}
                      selectedVariant={selectedVariant}
                    />
                  )}

                  {isSubscription && availableFrequencies.length > 0 && (
                    <FrequencyOptions
                      availableFrequencies={availableFrequencies}
                      handleFrequencyChange={handleFrequencyChange}
                      frequency={frequency}
                    />
                  )}

                  <div className={classes["product-hero-purchase-container"]}>
                    {!isSubscription && (
                      <ProductHeroQuantityInput
                        onDecrease={() => setQuantity(quantity > 1 ? quantity - 1 : 1)}
                        onIncrease={() => setQuantity(quantity + 1)}
                      onChange={(event) => {
                        const value = event.target.value === '' ? '' : Math.max(1, Number(event.target.value));
                        setQuantity(value);
                      }}
                      availableForSale={availableForSale}
                      value={quantity}
                    />
                    )}

                    <button
                      className={classes["add-to-basket-btn"]}
                      disabled={!availableForSale || quantity === 0}
                      onClick={addToCart}
                    >
                    {!availableForSale ? 'Sold Out' : 'Add to Basket'}
                    </button>

                    {availableForSale && quantity !== 0 && (
                      <button
                        className={`${classes["add-to-basket-btn"]} ${classes["buy-now-btn"]}`}
                        disabled={!availableForSale || quantity === 0}
                        onClick={handleBuyNow}
                      >
                        Buy Now
                      </button>
                    )}
                  </div>

                  <TrustBadge isMobile={isMobile} />

                  {whyBuyFromUsItems.length > 0 && (
                    <WhyBuyFromUs items={whyBuyFromUsItems} />
                  )}

                  {isSubscription && productInfoImages && (
                    <MainProductHeroImages
                      assets={productInfoImages.map(({ asset }) => asset)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default MainProductHero
